<script setup lang="ts">
// ================================
// L'ÉCHEC NOMMÉ QUI GARDE SES LEVIERS (DEV-1913, bloquant B9, constat F7.3).
//
// Ce que le client doit vivre : la cause vraie, et la possibilité d'agir tout de
// suite. C'est l'inverse de ce que le support a vécu quatorze fois ce semestre :
// un message générique, aucun levier, un courriel au support, un forçage manuel.
//
// Le composant ne grise rien et ne masque rien. Il affiche la cause servie et
// invite au levier que le moteur a nommé, quand il en nomme un. « Aucun levier »
// est une réponse pleine du moteur, pas un silence.
//
// DEV-2311, décision du 05/10/2026 : aucun code brut n'est montré à un client.
// Le code de la cause et la liste des segments couverts (codes C5, C4, C2) ne
// se lisent que depuis une session de l'équipe, sous une mention qui le dit.
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import {
    lireEchec,
    lireLeviersDuRefus,
} from "@modules/courtage/utils/optim-turpe-v2.echec"
import {
    anneeSeTerminantLe,
    periodeApresCollecteInterrompue,
    type PeriodeProposee,
} from "@modules/courtage/utils/optim-turpe-v2.fenetre"
import {
    ancreDepuisEchec,
    saisieNonServie,
} from "@modules/courtage/utils/optim-turpe-v2.completer"
import type { AncreAffiner } from "@modules/courtage/utils/optim-turpe-v2.completer"
import { estSessionDeLEquipe } from "@modules/courtage/utils/optim-turpe-v2.equipe"
import { ETAT_DU_MOTIF_D_ECHEC } from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            titre: "L'étude n'a pas abouti",
            // W89 (14/10/2026) : une panne se dit « pour une raison technique ».
            // Revue témoin du 15/10/2026 (D16) : le renvoi à l'aide n'est pas
            // écrit ici, le client final d'une marque blanche n'a pas cette
            // aide ; le composant Aide le porte selon la session.
            causeInconnue:
                "Cette étude a échoué pour une raison technique, sans lien avec vos données. Relancez l'étude.",
            code: "Réservé à l'équipe, cause : {code}",
            segmentsCouverts:
                "Réservé à l'équipe, segments couverts aujourd'hui : {liste}.",
            leviersActifs:
                "Les hypothèses de l'étude restent ouvertes : corrigez une valeur ou changez la période, puis relancez.",
            // L29 (23/09) : « si l'utilisateur veut compléter des informations
            // absentes il doit pouvoir ». Le geste ouvre le champ concerné dans
            // « Affiner l'étude » ; la saisie part comme surcharge tracée.
            completer: {
                structure: "Compléter la formule et les puissances souscrites",
                raccordement: "Compléter la puissance de raccordement",
                "heures-creuses": "Compléter les plages d'heures creuses",
                periode: "Changer la période analysée",
            },
            // Quand le moteur sert déjà le levier en toutes lettres, le bouton
            // n'en répète pas le texte : il mène seulement au bon groupe.
            completerParametres: "Corriger dans les hypothèses",
            // DEV-2163 : le refus porte sur la période DEMANDÉE, pas sur celle
            // de l'étude affichée avant.
            periodeDemandee: "Refus sur la période demandée : du {du} au {au}.",
            remede: "Pour lever ce refus : {remede}",
            // DEV-2164, cas 2.
            etendre: "Étendre à l'année",
            etendreAide: "Du {du} au {au}",
            // DEV-2164, cas 1.
            etudierPeriode: "Étudier cette période",
            periodeServie: "Meilleure période disponible : du {du} au {au}.",
            periodeDeduite:
                "Période proposée : du {du} au {au}, l'année qui se termine à la dernière donnée collectée.",
            donneesCompteur: "Voir les données de ce compteur",
        },
    },
})

type Props = { etude: OptimTurpeV2Etude }
const props = defineProps<Props>()

const emit = defineEmits<{
    completer: [ancre: AncreAffiner]
    /** Relancer l'étude sur une période proposée (DEV-2164, cas 1 et 2). */
    "etudier-periode": [periode: PeriodeProposee]
}>()

/** « 2026-02-28 » → « 28/02/2026 ». */
function jour(dateIso: string | undefined): string {
    const [a, m, j] = (dateIso ?? "").split("-")
    return a && m && j ? `${j}/${m}/${a}` : "—"
}

const getEchec = computed(() => lireEchec(props.etude))

/**
 * L'état du motif (Q28 : « À compléter », « Erreur de saisie », « Non
 * couvert », « Erreur technique ») ; un code que la table ne connaît pas n'en
 * porte aucun, la page ne le devine pas.
 */
const getEtatDuMotif = computed(() => {
    const code = getEchec.value?.code
    return code ? (ETAT_DU_MOTIF_D_ECHEC[code] ?? null) : null
})

/** Session de l'équipe (mention signée du jeton) : seule à lire les codes bruts. */
const getEquipe = computed(() => estSessionDeLEquipe())

/**
 * Les leviers servis avec le refus (`error.leviers`, courtage v154, DEV-2189) :
 * la période en bouton, le reste en phrase, dans l'ordre servi.
 */
const getLeviers = computed(() => lireLeviersDuRefus(props.etude))
const getLevierPeriodeServi = computed(() =>
    getLeviers.value.some((levier) => levier.periode !== null)
)

/**
 * Un levier client TEXTE est servi (la période est un bouton, pas un texte) :
 * la page n'ajoute ni son libellé générique ni un second texte.
 */
const getLevierClientServi = computed(() =>
    getLeviers.value.some((levier) => levier.periode === null)
)

/** Le champ que ce refus invite à compléter, ou `null` si la page ne sait pas. */
const getAncre = computed(() => ancreDepuisEchec(getEchec.value))

/**
 * La phrase à dire quand l'information manquante NE SE SAISIT PAS ici (segment,
 * branchement, maxima de puissance). On le dit plutôt que d'offrir un geste qui
 * ne mènerait nulle part.
 */
const getSaisieNonServie = computed(() => saisieNonServie(getEchec.value?.code))

/** La période demandée par le lancement refusé, quand elle est connue. */
const getPeriodeDemandee = computed(() => {
    const data = props.etude.inputs?.data
    return data?.date_from && data?.date_to ? data : null
})

/**
 * « Étendre à l'année » (DEV-2164, cas 2) : sur un refus de classes absentes,
 * l'année qui se termine à la fin de la période demandée.
 */
const getPeriodeAnnee = computed(() => {
    if (getEchec.value?.code !== "donnee-classe-hors-fenetre") return null
    const fin = getPeriodeDemandee.value?.date_to
    return fin ? anneeSeTerminantLe(fin) : null
})

/**
 * « Étudier cette période » (DEV-2164, cas 1) : sur une collecte interrompue,
 * la meilleure période servie par le moteur, sinon celle que la page déduit de
 * la date de dernière donnée, dite comme telle.
 */
const getPeriodeCollecte = computed(() => {
    const echec = getEchec.value
    if (!echec) return null
    // Le moteur sert déjà la période en levier : elle n'est pas proposée deux fois.
    if (getLevierPeriodeServi.value) return null
    // Servie avec le refus (`error.meilleure_periode`) : proposée quel que soit
    // le refus. Sinon, le repli de la page ne vaut que pour la collecte interrompue.
    const servie =
        echec.meilleurePeriode ??
        props.etude.qualite_donnees?.meilleure_periode ??
        null
    if (servie) return periodeApresCollecteInterrompue(echec.message, servie)
    if (echec.code !== "collecte-interrompue") return null
    return periodeApresCollecteInterrompue(echec.message, null)
})

/** La page des données du compteur, pour voir jusqu'où va l'historique. */
const getLienDonnees = computed(() => {
    const id = props.etude.inputs?.source?._id_source
    return getEchec.value?.code === "collecte-interrompue" && id
        ? `/source/${id}/data`
        : null
})
</script>

<template>
    <section
        v-if="getEchec"
        class="optim-turpe-v2-echec flex flex-col gap-2 rounded-lg border border-[var(--p-red-200)] bg-[var(--p-red-50)] p-5 sm:p-6"
        data-testid="optim-turpe-v2-echec"
    >
        <p
            class="flex flex-wrap items-center gap-2 text-sm font-medium text-[var(--p-red-900)]"
        >
            {{ t("titre") }}
            <span
                v-if="getEtatDuMotif"
                class="rounded-full border border-[var(--p-red-300)] bg-white px-2 py-0.5 text-xs font-medium text-[var(--p-red-800)]"
                data-testid="optim-turpe-v2-echec-etat"
                >{{ getEtatDuMotif }}</span
            >
        </p>

        <!-- La cause SERVIE d'abord. Jamais une cause fabriquée par l'écran. -->
        <p class="text-sm text-[var(--p-red-800)]">
            {{ getEchec.message ?? t("causeInconnue") }}
        </p>

        <!-- Le segment refusé et la liste des segments couverts, servis avec le
             refus par le moteur (DEV-1912) : jamais recopiés par la page. -->
        <p
            v-if="getEquipe && getEchec.segmentsCouverts?.length"
            class="text-xs text-[var(--p-red-700)]"
            data-testid="optim-turpe-v2-echec-segments-equipe"
        >
            {{
                t("segmentsCouverts", {
                    liste: getEchec.segmentsCouverts.join(", "),
                })
            }}
        </p>

        <!-- La période sur laquelle porte le refus (DEV-2163). -->
        <p
            v-if="getPeriodeDemandee"
            class="text-xs text-[var(--p-red-700)]"
            data-testid="optim-turpe-v2-echec-periode"
        >
            {{
                t("periodeDemandee", {
                    du: jour(getPeriodeDemandee.date_from),
                    au: jour(getPeriodeDemandee.date_to),
                })
            }}
        </p>

        <!-- Le remède, à côté de la cause (DEV-2163). -->
        <p
            v-if="getEchec.remede"
            class="text-sm text-[var(--p-red-900)]"
            data-testid="optim-turpe-v2-echec-remede"
        >
            {{ t("remede", { remede: getEchec.remede }) }}
        </p>

        <!-- Les leviers servis avec le refus (DEV-2189), dans l'ordre servi :
             la période en bouton, le reste en phrase. -->
        <template v-for="(levier, idx) in getLeviers" :key="`levier-${idx}`">
            <BaseButton
                v-if="levier.periode"
                :label="levier.libelle"
                icon="pi pi-calendar"
                size="small"
                class="w-fit"
                data-testid="optim-turpe-v2-echec-levier-periode"
                @click="emit('etudier-periode', levier.periode)"
            />
            <p
                v-else
                class="text-sm text-[var(--p-red-900)]"
                data-testid="optim-turpe-v2-echec-levier"
            >
                {{ levier.libelle }}
            </p>
        </template>

        <!-- « Étendre à l'année » (DEV-2164, cas 2). -->
        <div v-if="getPeriodeAnnee" class="flex flex-wrap items-center gap-2">
            <BaseButton
                :label="t('etendre')"
                icon="pi pi-calendar"
                size="small"
                class="w-fit"
                data-testid="optim-turpe-v2-echec-etendre"
                @click="emit('etudier-periode', getPeriodeAnnee)"
            />
            <span class="text-xs text-[var(--p-red-700)]">{{
                t("etendreAide", {
                    du: jour(getPeriodeAnnee.date_from),
                    au: jour(getPeriodeAnnee.date_to),
                })
            }}</span>
        </div>

        <!-- « Étudier cette période » (DEV-2164, cas 1). -->
        <div v-if="getPeriodeCollecte" class="flex flex-col gap-1">
            <p
                class="text-xs text-[var(--p-red-700)]"
                data-testid="optim-turpe-v2-echec-periode-proposee"
            >
                {{
                    t(
                        getPeriodeCollecte.servie
                            ? "periodeServie"
                            : "periodeDeduite",
                        {
                            du: jour(getPeriodeCollecte.periode.date_from),
                            au: jour(getPeriodeCollecte.periode.date_to),
                        }
                    )
                }}
            </p>
            <BaseButton
                :label="t('etudierPeriode')"
                icon="pi pi-calendar"
                size="small"
                class="w-fit"
                data-testid="optim-turpe-v2-echec-etudier-periode"
                @click="emit('etudier-periode', getPeriodeCollecte.periode)"
            />
        </div>
        <RouterLink
            v-if="getLienDonnees"
            :to="getLienDonnees"
            class="w-fit text-xs font-medium text-[var(--p-red-900)] underline"
            data-testid="optim-turpe-v2-echec-donnees"
        >
            {{ t("donneesCompteur") }}
        </RouterLink>

        <!-- Le levier nommé par le moteur, mis en avant. Les autres restent actifs. -->
        <p
            v-if="getEchec.levierLibelle && !getLevierClientServi"
            class="text-sm text-[var(--p-red-900)]"
        >
            {{ getEchec.levierLibelle }}
        </p>

        <!-- LE LEVIER « COMPLÉTER » (L29) : le geste ouvre le champ à saisir
             dans « Affiner l'étude », l'étude se relance sur la saisie. -->
        <BaseButton
            v-if="getAncre"
            :label="
                getLevierClientServi && !getLevierPeriodeServi
                    ? t('completerParametres')
                    : t(`completer.${getAncre}`)
            "
            icon="pi pi-pencil"
            size="small"
            class="w-fit"
            data-testid="optim-turpe-v2-echec-completer"
            @click="emit('completer', getAncre)"
        />

        <!-- L'information qui NE SE SAISIT PAS ici est nommée comme telle. -->
        <p
            v-if="getSaisieNonServie"
            class="text-xs text-[var(--p-red-700)]"
            data-testid="optim-turpe-v2-echec-saisie-non-servie"
        >
            {{ getSaisieNonServie }}
        </p>

        <!-- Saisies robustes (20/10/2026) : sur une cause qui ne se corrige
             pas depuis la page (levier « aucun »), inviter à corriger une
             valeur contredisait la phrase juste au-dessus. -->
        <p
            v-if="getEchec.levier !== 'aucun'"
            class="text-xs text-[var(--p-red-700)]"
            data-testid="optim-turpe-v2-echec-leviers-actifs"
        >
            {{ t("leviersActifs") }}
        </p>

        <!-- Le code servi, en dernier, pour l'équipe seulement (DEV-2311) :
             il lui permet de retrouver le cas sans aller-retour. -->
        <p
            v-if="getEquipe && getEchec.code"
            class="text-xs text-[var(--p-red-400)]"
            data-testid="optim-turpe-v2-echec-code-equipe"
        >
            {{ t("code", { code: getEchec.code }) }}
        </p>
    </section>
</template>
